import { Injectable, signal, computed, effect } from '@angular/core';

export type ThemeMode = 'light' | 'dark' | 'system';
export type Theme = 'light' | 'dark';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  readonly themeMode = signal<ThemeMode>(this.getInitialThemeMode());

  private readonly mediaQuery = typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)') : null;
  private readonly systemPrefersDark = signal<boolean>(this.mediaQuery?.matches ?? false);

  readonly currentTheme = computed<Theme>(() => {
    const mode = this.themeMode();
    if (mode === 'light' || mode === 'dark') {
      return mode;
    }
    return this.systemPrefersDark() ? 'dark' : 'light';
  });

  constructor() {
    if (this.mediaQuery) {
      this.mediaQuery.addEventListener('change', (e) => {
        this.systemPrefersDark.set(e.matches);
      });
    }

    // Apply DOM changes reactively whenever currentTheme changes
    effect(() => {
      const mode = this.themeMode();
      const theme = this.currentTheme();

      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('savinda_tms_theme', mode);
      }

      this.applyThemeToDom(theme);
    });

    // Ensure DOM is synced immediately on service instantiation
    this.applyThemeToDom(this.currentTheme());
  }

  setThemeMode(mode: ThemeMode): void {
    this.themeMode.set(mode);
  }

  toggleTheme(): void {
    const current = this.currentTheme();
    const next: ThemeMode = current === 'dark' ? 'light' : 'dark';
    this.setThemeMode(next);
  }

  private applyThemeToDom(theme: Theme): void {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    const body = document.body;

    if (theme === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
      root.setAttribute('data-theme', 'dark');
      root.style.colorScheme = 'dark';

      if (body) {
        body.classList.add('dark');
        body.classList.remove('light');
        body.setAttribute('data-theme', 'dark');
      }
    } else {
      root.classList.remove('dark');
      root.classList.add('light');
      root.setAttribute('data-theme', 'light');
      root.style.colorScheme = 'light';

      if (body) {
        body.classList.remove('dark');
        body.classList.add('light');
        body.setAttribute('data-theme', 'light');
      }
    }
  }

  private getInitialThemeMode(): ThemeMode {
    if (typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('savinda_tms_theme') as ThemeMode;
      if (saved === 'light' || saved === 'dark' || saved === 'system') {
        return saved;
      }
    }
    return 'dark';
  }
}


